import type { Context as ToneContext } from "tone";

type ToneModule = typeof import("tone");

/**
 * Propriétaire unique du `Tone.Context`.
 *
 * Tone tient un contexte global, remplacé à chaque appel de `Tone.setContext`.
 * Passé un `AudioContext` brut, `setContext` fabrique un **nouveau**
 * `Tone.Context` par appel (`Global.js` → `new Context(context)`), avec son
 * propre ticker Web Worker et le `lookAhead` remis à sa valeur par défaut.
 * Comme chaque synth appelait `setContext` dans son `init()`, on empilait un
 * wrapper par synth instancié — un par `SalamanderPianoSynth`, donc un par
 * morceau chargé — et les nœuds Tone créés avant continuaient de pointer sur
 * l'ancien.
 *
 * Ce module fait l'appel une fois par `AudioContext` et sert de point unique
 * pour régler la latence d'ordonnancement.
 */

/**
 * Fenêtre d'anticipation de l'ordonnanceur Tone, en secondes. **Zéro, pour
 * tout le monde.**
 *
 * # Ce qu'elle fait
 *
 * Tone programme les notes cette durée à l'avance, ce qui absorbe les hoquets
 * du fil principal : le fil temps réel a les échéances en main avant d'en avoir
 * besoin. Elle se paie en latence perçue sur les notes déclenchées *sans heure*
 * — `Tone.now()` vaut `currentTime + lookAhead`, donc une touche pressée sort
 * d'autant plus tard. Un temps absolu, lui, n'est pas décalé : la fenêtre ne
 * s'ajoute que dans `TimeBase._noArg()` (`context.now()`), et un instant
 * calculé depuis un tick est rendu tel quel. C'est pourquoi elle n'entre pas
 * dans ce qu'un partenaire entend d'une note programmée (cf. `heardMs`).
 *
 * # Pourquoi une valeur unique, et pourquoi zéro
 *
 * Elle était pilotée par le profil de perf — 10, 30 ou 60 ms selon la machine —
 * et le `PerfCoordinator` la remontait sur du jank. Ce lien est retiré : une
 * fenêtre déduite du *rendu* mesure la mauvaise chose (un appareil peut rendre
 * médiocrement et programmer impeccablement), et surtout le jeu à plusieurs
 * exige un budget **identique partout**, sinon c'est l'appareil le plus lent
 * qui impose son retard à toute la salle.
 *
 * Zéro parce que c'est le poste le plus lourd de ce budget et le seul qui soit
 * vraiment à nous : les deux autres — le réseau, et le tampon de sortie du
 * système — sont hors de portée du navigateur.
 *
 * # Ce qu'on accepte en échange
 *
 * Un hoquet du fil principal ne peut plus être absorbé : la note part en
 * retard, et plusieurs notes concernées sortent groupées au lieu de s'espacer.
 * Tone note aussi qu'une marge « réduit les clics » — programmer à
 * `currentTime` tout juste laisse Web Audio démarrer en milieu de tampon.
 *
 * C'est un pari assumé, et il est révocable en une ligne. La mesure qui le
 * trancherait pour de bon n'existe pas encore : compter les notes dont
 * l'instant visé était déjà passé dirait, elle, si la marge manque.
 */
export const LOOK_AHEAD_SECONDS = 0;

/**
 * Un wrapper par `AudioContext` rencontré, tant qu'on ne l'a pas libéré.
 *
 * Retenus tous, et non le seul courant : un synthé monté sur un nouveau
 * contexte — remontage du studio, HMR — pouvait basculer Tone avant que
 * l'ancien studio, démonté en asynchrone, n'appelle `releaseToneContext` sur
 * le sien. Le wrapper écrasé n'était alors plus disposable par personne, et
 * son ticker Web Worker tournait pour le reste de la vie de l'onglet.
 *
 * On ne dispose pas l'ancien à la bascule pour autant : `Context.dispose()`
 * ferme l'`AudioContext` sous-jacent (`close()` dans `Context.js`), et rien
 * ici ne sait si ce contexte a encore des nœuds vivants. C'est l'appelant qui
 * le sait, et c'est à lui de libérer — par `raw`, quel que soit le wrapper
 * devenu courant entre-temps.
 */
const wrappers = new Map<AudioContext, ToneContext>();
let current: ToneContext | null = null;

/**
 * Les neuf axes que `Tone.Listener` exige sous forme d'`AudioParam`.
 *
 * Tone construit son listener d'office à l'initialisation du contexte
 * (`onContextInit`), bien avant qu'on lui ait demandé quoi que ce soit de
 * spatial, et il enveloppe les neuf axes du listener **brut**. Firefox
 * n'implémente que les trois positions : `forwardX`…`upZ` y valent
 * `undefined`, le `Param` de Tone refuse de se construire (« param must be an
 * AudioParam »), et c'est l'initialisation entière du contexte qui échoue —
 * donc le premier `toDestination()` venu, pour une affaire de panoramique 3D
 * dont on ne se sert nulle part.
 *
 * `standardized-audio-context`, sous Tone, fabrique bien de faux paramètres
 * pour ce cas, mais seulement sur les contextes qu'il crée lui-même. Le nôtre
 * est un `AudioContext` natif, que Tone prend tel quel : `options.context`
 * devient `rawContext` sans enveloppe.
 */
const LISTENER_AXES = [
  "positionX",
  "positionY",
  "positionZ",
  "forwardX",
  "forwardY",
  "forwardZ",
  "upX",
  "upY",
  "upZ",
] as const;

/**
 * Complète l'`AudioListener` de `raw` avec de vrais `AudioParam` inertes.
 *
 * L'`offset` d'un `ConstantSourceNode` jamais démarré fait l'affaire : c'est
 * un `AudioParam` natif, donc accepté par Tone, et il ne pilote rien. Écrire
 * dedans ne déplacerait aucune oreille, ce qui est exactement ce qu'on veut :
 * personne n'écrit dedans.
 *
 * Silencieuse si le contexte n'expose pas de listener — un double de test, un
 * navigateur qui aurait les neuf axes.
 */
function shimListenerAxes(raw: AudioContext): void {
  const listener = raw.listener as unknown as Record<string, AudioParam | undefined> | undefined;

  if (listener === undefined || typeof raw.createConstantSource !== "function") {
    return;
  }

  for (const axis of LISTENER_AXES) {
    if (listener[axis] !== undefined) {
      continue;
    }

    Object.defineProperty(listener, axis, {
      value: raw.createConstantSource().offset,
      configurable: true,
    });
  }
}

/**
 * Installe le contexte Tone qui enveloppe `raw`, et le renvoie. Idempotent :
 * rappelée avec le même `AudioContext`, elle ne refabrique rien, et ne touche
 * au contexte global de Tone que s'il pointait ailleurs.
 *
 * Appelée avec un *autre* `AudioContext`, elle bascule sur son wrapper —
 * existant ou nouveau. L'ancien reste tenu jusqu'à `releaseToneContext`.
 */
export function ensureToneContext(Tone: ToneModule, raw: AudioContext): ToneContext {
  const existing = wrappers.get(raw);

  if (existing !== undefined) {
    if (current !== existing) {
      Tone.setContext(existing);
      current = existing;
    }

    return existing;
  }

  shimListenerAxes(raw);

  const context = new Tone.Context({ context: raw, lookAhead: LOOK_AHEAD_SECONDS });
  Tone.setContext(context);

  wrappers.set(raw, context);
  current = context;

  return context;
}

/** Fenêtre d'anticipation active, en secondes. */
export function getToneLookAhead(): number {
  return LOOK_AHEAD_SECONDS;
}

/**
 * Démonte le contexte Tone attaché à `raw` **et ferme `raw`**.
 *
 * `Context.dispose()` arrête le ticker, purge les timeouts en attente et
 * appelle `close()` sur l'`AudioContext` sous-jacent. L'appelant n'a donc
 * rien à fermer derrière : le faire quand même rejetterait sur un contexte
 * déjà fermé.
 */
export async function releaseToneContext(raw: AudioContext): Promise<void> {
  const context = wrappers.get(raw);

  if (context !== undefined) {
    wrappers.delete(raw);

    if (current === context) {
      current = null;
    }

    try {
      context.dispose();
    } catch (error) {
      console.warn("releaseToneContext: dispose failed", error);
    }

    return;
  }

  // Aucun synth n'a jamais chargé Tone : personne n'a enveloppé ce contexte,
  // on le ferme nous-mêmes.
  try {
    await raw.close();
  } catch (error) {
    console.warn("releaseToneContext: close failed", error);
  }
}

/** @internal Remet le module à zéro entre deux tests. */
export function __resetToneContextForTests(): void {
  wrappers.clear();
  current = null;
}
